import { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { AppLink } from "@/components/AppLink";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useStore } from "@/hooks/useStore";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";
import { structureProperty } from "@/endpoints/dryp/structureProperty";
import { ProgressBar } from "./ProgressBar";
import { PropertiesForm } from "./PropertiesForm";

type StructurePropertiesWizardProps = {
	onClose?: () => void;
	structureId?: Dryp.StructureId;
};

const StructurePropertiesWizardComponent = ({ onClose, structureId }: StructurePropertiesWizardProps) => {
	const {
		uiStore: { modalStore },
	} = useStore();
	const structuresApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);
	const propertiesApi = useAsyncStateStore(structureProperty.getForStructure);

	const savedAnyRef = useRef(false);
	// The number of structures needing configuration when the wizard opened — a stable
	// denominator for the progress bar, since the live count shrinks as we go.
	const initialRemainingRef = useRef<number | null>(null);

	// Only structures that still need configuring. Filling a structure's properties makes it
	// fully configured, so it drops out of this filter on the next fetch.
	const structureFilter = useMemo(
		() =>
			structureId
				? { isFullyConfiguredForIAndI: false, structureIds: [structureId] }
				: { isFullyConfiguredForIAndI: false, isActive: true },
		[structureId],
	);

	// Always fetch the first structure still needing configuration. After each save we call this
	// again: the just-configured structure is gone, so the next one surfaces and the count ticks down.
	const loadNext = useCallback(() => {
		structuresApi.fetch({ pageNumber: 0, pageSize: 1 }, "last30d", undefined, structureFilter).catch(() => {});
	}, [structureFilter]);

	useEffect(() => {
		loadNext();
	}, [loadNext]);

	useEffect(() => {
		return () => {
			if (savedAnyRef.current) onClose?.();
		};
	}, []);

	useEffect(() => {
		if (structuresApi.data && initialRemainingRef.current === null) {
			initialRemainingRef.current = structuresApi.data.query?.pagination?.totalItems ?? 0;
		}
	}, [structuresApi.data]);

	const currentStructure = structuresApi.data?.items?.[0]?.structure;
	const remaining = structuresApi.data?.query?.pagination?.totalItems ?? 0;
	const initialRemaining = initialRemainingRef.current ?? 0;
	const configured = Math.max(0, initialRemaining - remaining);

	useEffect(() => {
		if (!currentStructure) return;
		propertiesApi.fetch(currentStructure.id, { pageNumber: 0, pageSize: 100 }, undefined, undefined, {
			hasValue: false,
			requiredForIAndI: true,
			optionalForIAndI: true,
		});
	}, [currentStructure?.id]);

	const handleSaved = async () => {
		savedAnyRef.current = true;
		loadNext();
	};

	const isComplete = structuresApi.loaded && remaining === 0;
	const loading = structuresApi.loading || propertiesApi.loading;
	const showProgress = initialRemaining > 0;

	return (
		<>
			{showProgress && <ProgressBar completed={configured} total={initialRemaining} />}

			<DialogHeader>
				<DialogTitle>
					Configure{" "}
					{currentStructure ? (
						<AppLink
							to="Dashboard.Structures.Structure"
							params={{ id: currentStructure.id }}
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary hover:underline inline-flex items-center gap-1"
						>
							{currentStructure.name}
							<ExternalLink className="h-3.5 w-3.5" />
						</AppLink>
					) : (
						<span className="text-primary">structure</span>
					)}{" "}
					properties
				</DialogTitle>
				<DialogDescription>Add the required parameters this structure is missing.</DialogDescription>
			</DialogHeader>

			{loading ? (
				<div className="space-y-3 py-2">
					<Skeleton className="h-9 w-full rounded" />
					<Skeleton className="h-9 w-full rounded" />
				</div>
			) : isComplete ? (
				<div className="flex flex-col items-center gap-2 py-8 text-center">
					<CheckCircle2 className="h-10 w-10 text-emerald-500" />
					<p className="text-sm font-medium">All set</p>
					{configured > 0 ? (
						<p className="text-xs text-muted-foreground">
							Configured {configured} structure{configured === 1 ? "" : "s"}.
						</p>
					) : (
						<p className="text-xs text-muted-foreground">No structures need configuration.</p>
					)}
					<Button className="mt-2" onClick={() => modalStore.close()}>
						Done
					</Button>
				</div>
			) : currentStructure ? (
				<PropertiesForm
					key={currentStructure.id}
					structureId={currentStructure.id}
					properties={propertiesApi.data?.items ?? []}
					onSaved={handleSaved}
				/>
			) : (
				<p className="rounded-md bg-muted/40 px-3 py-6 text-center text-sm text-muted-foreground">
					No structures need configuration.
				</p>
			)}
		</>
	);
};

export const StructurePropertiesWizard = observer(StructurePropertiesWizardComponent);
